import { useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { Trans } from "@lingui/react/macro";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { cn } from "../lib/cn";
import { Button } from "./button";

export type LookupProps<T> = {
  id?: string;
  /** What is typed; the page searches with it (debounced) and passes back `options`. */
  query: string;
  onQueryChange: (query: string) => void;
  /** The matches for the query; undefined until the first answer. */
  options: readonly T[] | undefined;
  optionKey: (option: T) => string;
  /** One match: its name on the first line, what tells it apart (code, address) on a second, smaller one. */
  renderOption: (option: T) => ReactNode;
  onPick: (option: T) => void;
  /** The choice made, shown in place of the field with Change beside it. */
  picked?: ReactNode;
  onChange?: () => void;
  loading?: boolean;
  /** Under the matches when there are none, or instead of them when the search failed: what to do instead. */
  empty?: ReactNode;
  placeholder?: string;
  invalid?: boolean;
  className?: string;
};

/**
 * Find one record by typing and pick it: a store, a person. The field is an Input with a magnifier; the matches open under
 * it like a Menu (sharp, a hairline border, the overlay shadow, no motion). Arrow keys move, Enter picks, Escape closes.
 * Once picked, the field becomes the choice with Change beside it. The page owns the search; this owns the keyboard.
 */
export function Lookup<T>({
  id,
  query,
  onQueryChange,
  options,
  optionKey,
  renderOption,
  onPick,
  picked,
  onChange,
  loading = false,
  empty,
  placeholder,
  invalid,
  className,
}: LookupProps<T>) {
  const list = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const matches = options ?? [];
  const showing = open && query.trim().length > 0 && (options !== undefined || loading || empty !== undefined);

  if (picked !== undefined && picked !== null) {
    return (
      <div className={cn("flex min-h-9 items-center justify-between gap-3 border border-line-strong bg-surface py-1.5 ps-3 pe-1.5", invalid && "border-danger", className)}>
        <div className="min-w-0 text-base text-ink">{picked}</div>
        {onChange && (
          <Button size="sm" variant="ghost" onClick={onChange}>
            <Trans>Change</Trans>
          </Button>
        )}
      </div>
    );
  }

  const pick = (option: T) => {
    onPick(option);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      if (matches.length > 0) setActive((i) => (i + (event.key === "ArrowDown" ? 1 : matches.length - 1)) % matches.length);
    } else if (event.key === "Enter" && showing && matches[active]) {
      event.preventDefault();
      pick(matches[active]);
    } else if (event.key === "Escape" && showing) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <MagnifyingGlass aria-hidden className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" />
      <input
        id={id}
        role="combobox"
        aria-expanded={showing}
        aria-controls={list}
        aria-autocomplete="list"
        aria-activedescendant={showing && matches[active] ? `${list}-${active}` : undefined}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          onQueryChange(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className={cn(
          "h-9 w-full border border-line-strong bg-surface pe-3 ps-9 text-base text-ink transition-colors duration-150 ease-out-quick",
          "placeholder:text-ink-4 hover:border-ink-3 focus-visible:border-ink focus-visible:outline-none aria-invalid:border-danger",
        )}
      />
      {showing && (
        <div className="absolute inset-x-0 top-full z-50 mt-1 border border-line-strong bg-surface py-1 shadow-overlay">
          <ul id={list} role="listbox" className="max-h-72 overflow-y-auto">
            {matches.map((option, i) => (
              <li
                key={optionKey(option)}
                id={`${list}-${i}`}
                role="option"
                aria-selected={i === active}
                // Keeps the focus in the field, so the click lands before the blur closes the list.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(option)}
                onMouseMove={() => setActive(i)}
                className={cn("cursor-default px-3 py-2 text-sm text-ink-2 transition-colors duration-150 ease-out-quick", i === active && "bg-surface-2 text-ink")}
              >
                {renderOption(option)}
              </li>
            ))}
          </ul>
          {matches.length === 0 && loading && (
            <p className="px-3 py-2 text-sm text-ink-3">
              <Trans>Searching…</Trans>
            </p>
          )}
          {matches.length === 0 && !loading && (
            <div className="px-3 py-2 text-sm text-ink-3" onMouseDown={(e) => e.preventDefault()}>
              {empty ?? <Trans>Nothing matches.</Trans>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
